// Survey administrator Report Summary (v6.5, 2026-05-24).
//
// Three sections per survey:
//   1. Flagged comments (HostileCommentsAdmin) -- list of every submitted
//      peer-feedback comment the AI safety screen tagged as HOSTILE.
//   2. Per respondent: the full individual AI-synthesized report
//      (ReportTeamPlayerSummarySection) -- Section A header, PLAHER radar
//      chart, Section B paragraphs, Section C themes with Justification +
//      Recommendations split, Section D next direction, Section E
//      strengths, plus the deterministic detailed scores + peer comments.
//      Each respondent's section is identical to what they see on their
//      own report page (admins get full visibility).
//   3. Per respondent: submitted feedback -- the qualitative comments
//      THIS respondent wrote about each teammate. Lets the administrator
//      see what each student actually said, not just what was said about
//      them. Empty when the respondent didn't write anything.

/* eslint-disable react/no-array-index-key */
import React from 'react';
import { Card } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { HostileCommentsAdmin } from './HostileCommentsAdmin';
import { ReportTeamPlayerSummarySection } from './ReportTeamPlayerSummarySection';
import { SurveyReportDashboard } from './SurveyReportDashboard';

const TEAMPLAYER_TEMPLATES = new Set([
  'teamPlayer',
  'teamPlayerTeamDynamics',
  'teamPlayerTeamDynamics2026',
]);

const safeText = (v) => (typeof v === 'string' ? v.trim() : '');

// Pull the qualitative comments this respondent submitted ABOUT each
// teammate. The response is shaped `response.Qualitative[peerId]`.
// 2026-06-05: 'N/A' (set by the Not familiar checkbox) is preserved in
// this list but rendered as a dedicated "marked Not familiar" entry
// rather than as a fake comment, so the instructor sees who the rater
// opted out on without the text 'N/A' showing as their written feedback.
function listSubmittedQualitative(respondent, respondents) {
  if (!respondent || !respondent.response) return [];
  const map = respondent.response.Qualitative || {};
  const byId = {};
  (respondents || []).forEach((r) => { byId[r.respondentId] = r; });
  return Object.entries(map)
    .filter(([targetId, text]) => targetId !== respondent.respondentId && safeText(text))
    .map(([targetId, text]) => {
      const target = byId[targetId];
      const trimmed = safeText(text);
      return {
        targetId,
        targetName: target
          ? `${target.firstName || ''} ${target.lastName || ''}`.trim() || (target.email || '')
          : '(unknown teammate)',
        text: trimmed,
        notFamiliar: trimmed === 'N/A',
      };
    });
}

function SubmittedFeedbackBlock({ respondent, respondents }) {
  const items = listSubmittedQualitative(respondent, respondents);
  if (items.length === 0) return null;
  return (
    <Card className="py-3 my-3">
      <Card.Body>
        <h4 className="mb-3">
          {`Submitted feedback by ${respondent.firstName || ''} ${respondent.lastName || ''}`.trim()}
        </h4>
        <p className="text-muted small">
          The qualitative comments this respondent submitted about each
          teammate (final text after any coaching iterations).
        </p>
        {items.map((it, idx) => (
          <div
            key={`submitted-${respondent.respondentId}-${idx}`}
            className="border-start border-3 ps-3 my-3"
          >
            <div className="text-muted small mb-1">
              {`About ${it.targetName}`}
            </div>
            {it.notFamiliar ? (
              <div className="text-muted fst-italic">
                (marked &quot;Not familiar with this team member&apos;s behavior&quot;)
              </div>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap' }}>{it.text}</div>
            )}
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}
SubmittedFeedbackBlock.propTypes = {
  respondent: PropTypes.object.isRequired,
  respondents: PropTypes.array,
};
SubmittedFeedbackBlock.defaultProps = { respondents: [] };

function SurveyReportBody({ respondents, survey, template }) {
  // v6.5 hotfix (2026-05-24): also check survey.templateId. The /api/survey/:id
  // route's `template` projection initially omitted templateId, so this check
  // silently fell into the "calculator not wired" branch. survey.templateId is
  // always populated; template.templateId is now also populated by the route,
  // and we accept either.
  const templateId = (template && template.templateId) || (survey && survey.templateId);
  const isTeamPlayer = TEAMPLAYER_TEMPLATES.has(templateId);

  return (
    <>
      <Card className="pagebreak py-3 my-3">
        <Card.Body>
          <h2>Survey Report Summary</h2>
          <h3>{survey.name}</h3>
          <div className="text-muted">{survey.createdBy}</div>
        </Card.Body>
      </Card>

      {/* v6.6 (2026-05-24): dashboard at the top with stat tiles + per-team
          respondent table. Each respondent name is an anchor link that
          deep-jumps to the corresponding individual report card below.
          The "Hostile Comments" stat tile links to #flagged-comments. */}
      <SurveyReportDashboard respondents={respondents} survey={survey} />

      {survey && survey.surveyId ? (
        // v6.6: anchor id added so the dashboard's "Hostile Comments" stat
        // tile can deep-link to this card via href="#flagged-comments".
        <div id="flagged-comments">
          <HostileCommentsAdmin surveyId={survey.surveyId} />
        </div>
      ) : null}

      {(respondents || []).map((respondent) => (
        // v6.6: anchor id added per respondent so the dashboard table can
        // deep-link to each individual report card.
        <div
          key={`survey-report-respondent-${respondent.respondentId}`}
          id={`respondent-${respondent.respondentId}`}
        >
          {isTeamPlayer ? (
            <ReportTeamPlayerSummarySection respondent={respondent} />
          ) : (
            <Card className="pagebreak py-3 my-3">
              <Card.Body>
                <h4>
                  {`Team ${respondent.team || ''}: ${respondent.firstName || ''} ${respondent.lastName || ''}`.trim()}
                </h4>
                <p className="text-muted small">
                  This template does not yet have a calculator wired up;
                  per-respondent synthesis is not available.
                </p>
              </Card.Body>
            </Card>
          )}
          <SubmittedFeedbackBlock
            respondent={respondent}
            respondents={respondents}
          />
        </div>
      ))}
    </>
  );
}

SurveyReportBody.propTypes = {
  respondents: PropTypes.array,
  survey: PropTypes.object,
  template: PropTypes.object,
};

SurveyReportBody.defaultProps = {
  respondents: [],
  survey: {},
  template: {},
};

export default SurveyReportBody;
